import { useMemo } from "react";
import { useColorMode } from "src/theme";
import { HealthStatus } from "./health";
import { WAVE_COLOR } from "./waves";

/**
 * Theme-aware chart palette.
 *
 * OUI 1.19 ships no JS theme tokens (no `useEuiTheme`); light vs. dark is a
 * runtime stylesheet swap owned by `useColorMode()`. chart.js draws to a canvas
 * and can't read OUI CSS, so we centralise the few colors charts need here and
 * key them off the active color mode. Data-series hues are the OUI semantic
 * palette (stable across both themes); only text/grid/surface colors flip.
 */

export interface ChartColors {
  /** Axis tick + legend label color. */
  text: string;
  /** Muted/subdued text. */
  textSubdued: string;
  /** Grid line color. */
  grid: string;
  /** Donut/segment border color (matches the panel surface). */
  surface: string;
  /** Tooltip background. */
  tooltipBg: string;
  /** OUI semantic data colors, theme-stable. */
  success: string;
  warning: string;
  danger: string;
  primary: string;
  accent: string;
  subdued: string;
  /** Health-status → color, aligned with HEALTH_COLOR semantic names. */
  health: Record<HealthStatus, string>;
  /** Wave → color, matching the table's wave badges. */
  wave: Record<string, string>;
}

const SEMANTIC = {
  success: "#00bfb3",
  warning: "#fec514",
  danger: "#bd271e",
  primary: "#0077cc",
  accent: "#dd0a73",
  subdued: "#98a2b3",
};

// Wave colours come from the shared palette so the donut matches the badges.
const WAVE: Record<string, string> = WAVE_COLOR;

export const useChartColors = (): ChartColors => {
  const { colorMode } = useColorMode();
  return useMemo<ChartColors>(() => {
    const dark = colorMode === "dark";
    return {
      text: dark ? "rgba(255,255,255,0.7)" : "rgba(52,55,65,0.85)",
      textSubdued: dark ? "rgba(255,255,255,0.45)" : "rgba(105,112,125,0.8)",
      grid: dark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)",
      surface: dark ? "#1d1e24" : "#ffffff",
      tooltipBg: dark ? "rgba(20,22,28,0.95)" : "rgba(255,255,255,0.97)",
      ...SEMANTIC,
      health: {
        healthy: SEMANTIC.success,
        warning: SEMANTIC.warning,
        critical: SEMANTIC.danger,
        unknown: SEMANTIC.subdued,
      },
      wave: WAVE,
    };
  }, [colorMode]);
};
